import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ListAlt as ListAltIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import CollectorHome from './CollectorHome';
import FieldOfficerDashboard from './FieldOfficerDashboard';
import MyCollections from './MyCollections';
import CollectedInstallments from './CollectedInstallments';
import PaymentHistory from './PaymentHistory';
import LoanSchedules from './LoanSchedules';
import OverdueInstallments from './OverdueInstallments';
import OnlineStatusIndicator from '../Common/OnlineStatusIndicator';

const drawerWidth = 260;

const CollectorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');

  useEffect(() => {
    const path = location.pathname.split('/').pop() || 'dashboard';
    setSelectedMenu(path);
  }, [location]);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: 'dashboard' },
    { text: 'Search & Collect', icon: <SearchIcon />, path: 'field-officer' },
    { text: 'Due Today', icon: <ListAltIcon />, path: 'collections' },
    { text: 'Payment History', icon: <HistoryIcon />, path: 'payment-history' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (item) => {
    setSelectedMenu(item.path);
    navigate(`/collector/${item.path}`);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
          color: 'white',
          minHeight: { xs: '56px', sm: '64px' },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.125rem' },
          }}
        >
          Field Officer Panel
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ overflow: 'auto', mt: 1 }}>
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={selectedMenu === item.path}
                onClick={() => handleMenuClick(item)}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565C0 0%, #0A3D91 100%)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '& .MuiListItemText-primary': {
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: selectedMenu === item.path ? 'white' : '#1976D2',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem' },
                    fontWeight: selectedMenu === item.path ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F5F7FA' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.3)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Field Officer Dashboard
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: 'block' },
              fontWeight: 500,
              fontSize: { sm: '0.875rem', md: '1rem' },
            }}
          >
            {user?.fullName}
          </Typography>
          <Button 
            color="inherit" 
            onClick={handleLogout} 
            startIcon={<LogoutIcon />}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Logout
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Out
            </Box>
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: '280px', sm: drawerWidth },
              borderRight: '1px solid rgba(25, 118, 210, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid rgba(25, 118, 210, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          maxWidth: '100%',
          overflowX: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }} />
        <OnlineStatusIndicator />
        <Routes>
          <Route path="" element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<CollectorHome />} />
          <Route path="field-officer" element={<FieldOfficerDashboard />} />
          <Route path="collections" element={<MyCollections />} />
          <Route path="overdue" element={<OverdueInstallments />} />
          <Route path="collected-installments" element={<CollectedInstallments />} />
          <Route path="payment-history" element={<PaymentHistory />} />
          <Route path="loan-schedules" element={<LoanSchedules />} />
          <Route path="loan-schedules/:loanId" element={<LoanSchedules />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default CollectorDashboard;

